import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  CheckCircle2,
  Loader2,
  Sparkles,
  ArrowRight,
  ArrowLeft,
  Trophy,
  Zap,
  ShieldCheck,
  MessageCircle,
} from "lucide-react";
import { openLiveChat } from "../../lib/chat";
import { OLIVEUP_MAILTO } from "../../lib/contact";
import { prequalify } from "../../lib/prequalify";
import posthog from "posthog-js";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const productOptions = [
  { value: "sba_loans", label: "SBA Loans ($25K – $5M)" },
  { value: "merchant_cash_advance", label: "MCA Loans ($5K – $500K)" },
  { value: "line_of_credit", label: "Lines of Credit ($10K – $250K)" },
  { value: "equipment_financing", label: "Equipment Financing ($5K – $2M)" },
  { value: "refinancing", label: "Refinancing (up to $5M)" },
  { value: "working_capital", label: "Working Capital ($5K – $500K)" },
  { value: "invoice_factoring", label: "Invoice Factoring ($10K – $5M)" },
  { value: "not_sure", label: "Not sure — help me figure it out" },
];

const timeOptions = [
  { value: "<6 months", label: "Less than 6 months" },
  { value: "6-12 months", label: "6 – 12 months" },
  { value: "1-2 years", label: "1 – 2 years" },
  { value: "2-5 years", label: "2 – 5 years" },
  { value: "5+ years", label: "5+ years" },
];

const revenueOptions = [
  { value: "<10k", label: "Under $10K / month" },
  { value: "10-25k", label: "$10K – $25K / month" },
  { value: "25-75k", label: "$25K – $75K / month" },
  { value: "75-250k", label: "$75K – $250K / month" },
  { value: "250k+", label: "$250K+ / month" },
];

const amountOptions = [
  { value: "<25k", label: "Under $25K" },
  { value: "25-100k", label: "$25K – $100K" },
  { value: "100-500k", label: "$100K – $500K" },
  { value: "500k-1m", label: "$500K – $1M" },
  { value: "1m+", label: "$1M+" },
];

const urgencyOptions = [
  { value: "asap", label: "ASAP — within 48 hours" },
  { value: "this_week", label: "Within a week" },
  { value: "this_month", label: "Within a month" },
  { value: "exploring", label: "Just exploring options" },
];

const initialForm = {
  business_name: "",
  owner_name: "",
  email: "",
  phone: "",
  business_type: "",
  time_in_business: "",
  monthly_revenue: "",
  requested_amount: "",
  funding_product: "",
  funding_urgency: "",
  notes: "",
};

const TIER_HEADLINE = {
  premium: "Premium match!",
  strong: "Strong match!",
  match: "You pre-qualify!",
  review: "We'll find you something.",
};

const TIER_SUB = {
  premium:
    "Your business looks great on paper. Lenders are going to want to talk to you.",
  strong:
    "Multiple lenders in our network would love to fund a business like yours.",
  match:
    "We found options that fit your business. Lock them in below.",
  review:
    "Your profile needs a human touch — and that's our specialty. Drop your contact info and a real advisor will reach out with a custom plan.",
};

export default function ApplicationForm() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [prequal, setPrequal] = useState(null);

  const update = (key) => (val) =>
    setForm((f) => ({
      ...f,
      [key]: typeof val === "string" ? val : val.target.value,
    }));

  const validateStep1 = () => {
    const e = {};
    if (!form.time_in_business) e.time_in_business = "Pick one";
    if (!form.monthly_revenue) e.monthly_revenue = "Pick one";
    if (!form.requested_amount) e.requested_amount = "Pick one";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep3 = () => {
    const e = {};
    if (!form.business_name.trim()) e.business_name = "Required";
    if (!form.owner_name.trim()) e.owner_name = "Required";
    if (!form.email.trim()) e.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Looks off. Double-check?";
    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 7)
      e.phone = "Need a real number";
    if (!form.business_type.trim()) e.business_type = "Required";
    if (!form.funding_product) e.funding_product = "Pick one";
    if (!form.funding_urgency) e.funding_urgency = "Pick one";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handlePrequalify = (ev) => {
    ev?.preventDefault?.();
    if (!validateStep1()) {
      toast.error("Just need answers to those three quick ones.");
      return;
    }
    posthog.capture("prequal_started", {
      time_in_business: form.time_in_business,
      monthly_revenue: form.monthly_revenue,
      requested_amount: form.requested_amount,
    });
    const result = prequalify({
      time_in_business: form.time_in_business,
      monthly_revenue: form.monthly_revenue,
      requested_amount: form.requested_amount,
    });
    posthog.capture("prequal_completed", {
      tier: result.tier,
      matched_count: result.matched.length,
      time_in_business: form.time_in_business,
      monthly_revenue: form.monthly_revenue,
      requested_amount: form.requested_amount,
    });
    setPrequal(result);
    setStep(2);
    toast.success("Pre-qualification complete in 38 seconds. Nice work.");
  };

  const handleContinueToForm = () => {
    posthog.capture("application_form_started", {
      tier: prequal?.tier,
      matched_count: prequal?.matched?.length ?? 0,
      time_in_business: form.time_in_business,
      monthly_revenue: form.monthly_revenue,
      requested_amount: form.requested_amount,
    });
    setStep(3);
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validateStep3()) {
      toast.error("Almost there! A few fields need your attention.");
      return;
    }
    setSubmitting(true);
    const distinctId = posthog.get_distinct_id();
    try {
      await axios.post(`${API}/funding-applications`, form, {
        headers: { "X-POSTHOG-DISTINCT-ID": distinctId },
      });
      posthog.identify(distinctId, { email: form.email });
      posthog.capture("application_submitted", {
        funding_product: form.funding_product,
        funding_urgency: form.funding_urgency,
        time_in_business: form.time_in_business,
        monthly_revenue: form.monthly_revenue,
        requested_amount: form.requested_amount,
        business_type: form.business_type,
      });
      setSubmitted(true);
      toast.success("Locked in. A real human will reach out shortly.");
    } catch (err) {
      console.error(err);
      posthog.capture("application_submission_failed", {
        error_status: err?.response?.status ?? null,
      });
      toast.error("Hmm, that didn't go through. Try again or chat with us.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm(initialForm);
    setPrequal(null);
    setErrors({});
    setStep(1);
    setSubmitted(false);
  };

  return (
    <section
      id="apply"
      data-testid="application-section"
      className="relative py-24 md:py-32 bg-primary text-primary-foreground overflow-hidden"
    >
      <div className="blob absolute -top-20 -right-20 h-[26rem] w-[26rem] rounded-full bg-[#E07A5F]/40" aria-hidden />
      <div className="blob absolute -bottom-32 -left-20 h-[26rem] w-[26rem] rounded-full bg-[#A6B569]/40" aria-hidden />

      <div className="container-tight relative grid lg:grid-cols-12 gap-12">
        {/* Left intro */}
        <div className="lg:col-span-5">
          <span className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-[#F5B82E]">
            <Sparkles className="h-3.5 w-3.5" /> 01. Get Started
          </span>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-balance">
            Ready to Get <span className="text-[#F5B82E] italic">Olive</span>{" "}
            That Funding?
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-primary-foreground/85">
            Join 10,000+ businesses who said <em>YES</em> to growth. Three quick
            questions get you a pre-qualification estimate in under 2 minutes —
            then we connect you with a real human within 2 hours.
          </p>

          <ul className="mt-8 space-y-3">
            {[
              "Instant pre-qualification — no credit ding",
              "Soft pull only, even after you submit",
              "Real offers from real lenders",
              "A human advisor assigned to your case",
            ].map((b) => (
              <li key={b} className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-[#F5B82E] shrink-0 mt-0.5" />
                <span className="text-primary-foreground/90">{b}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Right wizard */}
        <div className="lg:col-span-7">
          <div className="bg-background text-foreground rounded-3xl p-7 md:p-10 shadow-[0_20px_60px_rgb(0,0,0,0.18)]">
            {submitted ? (
              <SuccessState onReset={reset} prequal={prequal} />
            ) : (
              <>
                <Stepper step={step} />
                {step === 1 && (
                  <Step1Prequal
                    form={form}
                    errors={errors}
                    update={update}
                    onSubmit={handlePrequalify}
                  />
                )}
                {step === 2 && prequal && (
                  <Step2Result
                    prequal={prequal}
                    onContinue={handleContinueToForm}
                    onBack={() => setStep(1)}
                  />
                )}
                {step === 3 && (
                  <Step3Contact
                    form={form}
                    errors={errors}
                    update={update}
                    onSubmit={handleSubmit}
                    onBack={() => setStep(2)}
                    submitting={submitting}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------- Subcomponents -------------------- */

function Stepper({ step }) {
  const labels = ["Quick check", "Your offers", "Lock it in"];
  return (
    <div data-testid="wizard-stepper" className="flex items-center gap-3 mb-7">
      {labels.map((l, i) => {
        const idx = i + 1;
        const active = idx === step;
        const done = idx < step;
        return (
          <div key={l} className="flex items-center gap-3 flex-1">
            <div
              className={`flex items-center gap-2 ${
                active ? "text-foreground" : done ? "text-primary" : "text-muted-foreground"
              }`}
            >
              <div
                className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold ${
                  active
                    ? "bg-[#F5B82E] text-foreground"
                    : done
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                {done ? <CheckCircle2 className="h-4 w-4" /> : idx}
              </div>
              <span className="text-xs sm:text-sm font-semibold whitespace-nowrap">
                {l}
              </span>
            </div>
            {i < labels.length - 1 && (
              <div className="flex-1 h-px bg-border min-w-3" />
            )}
          </div>
        );
      })}
    </div>
  );
}

function Step1Prequal({ form, errors, update, onSubmit }) {
  return (
    <form onSubmit={onSubmit} data-testid="prequal-step-1" noValidate>
      <h3 className="font-display text-2xl font-bold mb-1">
        Three quick questions.
      </h3>
      <p className="text-sm text-muted-foreground mb-7">
        We&apos;ll use these to estimate your offers instantly. Soft pull only
        — no impact on your credit.
      </p>

      <div className="grid gap-5">
        <Field label="How long has your business been operating?" error={errors.time_in_business}>
          <SelectField
            testId="select-time"
            placeholder="Pick one…"
            value={form.time_in_business}
            onChange={update("time_in_business")}
            options={timeOptions}
          />
        </Field>
        <Field label="What's your average monthly revenue?" error={errors.monthly_revenue}>
          <SelectField
            testId="select-revenue"
            placeholder="Pick one…"
            value={form.monthly_revenue}
            onChange={update("monthly_revenue")}
            options={revenueOptions}
          />
        </Field>
        <Field label="How much funding are you looking for?" error={errors.requested_amount}>
          <SelectField
            testId="select-amount"
            placeholder="Pick one…"
            value={form.requested_amount}
            onChange={update("requested_amount")}
            options={amountOptions}
          />
        </Field>
      </div>

      <Button
        type="submit"
        data-testid="prequalify-btn"
        className="mt-8 w-full h-14 rounded-full bg-accent hover:bg-[#D1664A] text-accent-foreground hover:text-accent-foreground text-base font-semibold shadow-[0_4px_14px_0_rgb(224,122,95,0.39)] hover:-translate-y-0.5 transition-all"
      >
        See my offers
        <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
      <p className="text-xs text-muted-foreground text-center mt-4">
        Soft pull only. No application fees. No spam.
      </p>
      <div className="mt-6 rounded-xl border border-border bg-muted/50 px-4 py-3 text-center">
        <p className="text-xs text-muted-foreground">
          Stuck or want a human to sanity-check before you continue?
        </p>
        <button
          type="button"
          onClick={openLiveChat}
          data-testid="prequal-chat-fallback"
          className="mt-2 inline-flex items-center justify-center gap-2 text-sm font-semibold text-primary hover:text-[#3B4A1C] transition-colors"
        >
          <MessageCircle className="h-4 w-4" />
          Open live chat
        </button>
      </div>
    </form>
  );
}

function Step2Result({ prequal, onContinue, onBack }) {
  const isReview = prequal.tier === "review";
  return (
    <div data-testid="prequal-result">
      <div className="flex items-start gap-3 mb-2">
        <div className="h-12 w-12 rounded-2xl bg-[#F5B82E]/15 flex items-center justify-center text-[#C9951E] shrink-0">
          {isReview ? (
            <Sparkles className="h-6 w-6" />
          ) : (
            <Trophy className="h-6 w-6" />
          )}
        </div>
        <div>
          <h3 data-testid="prequal-tier-headline" className="font-display text-2xl md:text-3xl font-bold leading-tight">
            {TIER_HEADLINE[prequal.tier]}
          </h3>
          <p className="text-sm text-muted-foreground mt-1.5">
            {TIER_SUB[prequal.tier]}
          </p>
        </div>
      </div>

      {!isReview && (
        <>
          {/* Estimate band */}
          <div className="mt-6 grid grid-cols-2 gap-3">
            <div className="rounded-2xl bg-secondary p-5">
              <span className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                Estimated up to
              </span>
              <div
                data-testid="prequal-estimated-max"
                className="font-display text-3xl font-extrabold text-primary mt-1"
              >
                {prequal.estimatedMaxLabel}
              </div>
            </div>
            <div className="rounded-2xl bg-secondary p-5">
              <span className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                Estimated rate
              </span>
              <div className="font-display text-3xl font-extrabold text-primary mt-1">
                {prequal.rateLow}–{prequal.rateHigh}%
              </div>
            </div>
          </div>

          {/* Matched products */}
          <div className="mt-6">
            <span className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground">
              Programs you qualify for
            </span>
            <div data-testid="prequal-matched" className="mt-3 flex flex-wrap gap-2">
              {prequal.matched.map((p) => (
                <span
                  key={p}
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary text-xs sm:text-sm font-semibold px-3.5 py-2"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {p}
                </span>
              ))}
            </div>
          </div>

          {/* Trust strip */}
          <div className="mt-6 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <Zap className="h-3.5 w-3.5 text-[#F5B82E]" /> No credit pull
            </span>
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-[#F5B82E]" /> Estimates only — final terms set by lender
            </span>
          </div>
        </>
      )}

      {/* CTAs */}
      <div className="mt-8 flex flex-col sm:flex-row-reverse gap-3">
        <Button
          onClick={onContinue}
          data-testid="continue-to-contact-btn"
          className="flex-1 h-14 rounded-full bg-accent hover:bg-[#D1664A] text-accent-foreground hover:text-accent-foreground text-base font-semibold shadow-[0_4px_14px_0_rgb(224,122,95,0.39)] hover:-translate-y-0.5 transition-all"
        >
          {isReview ? "Talk to a human" : "Lock in my offers"}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
        <Button
          onClick={onBack}
          variant="outline"
          data-testid="back-to-prequal-btn"
          className="rounded-full h-14 px-6 text-base font-semibold"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
      </div>
    </div>
  );
}

function Step3Contact({ form, errors, update, onSubmit, onBack, submitting }) {
  return (
    <form onSubmit={onSubmit} data-testid="funding-application-form" noValidate>
      <h3 className="font-display text-2xl font-bold mb-1">
        Lock it in. We&apos;ll do the rest.
      </h3>
      <p className="text-sm text-muted-foreground mb-7">
        A real human reviews this within 2 hours during business hours and
        reaches out by phone or email.
      </p>

      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Business name" error={errors.business_name}>
          <Input
            data-testid="input-business-name"
            value={form.business_name}
            onChange={update("business_name")}
            placeholder="Olive's Coffee Co."
            className="h-12 rounded-xl"
          />
        </Field>
        <Field label="Your name" error={errors.owner_name}>
          <Input
            data-testid="input-owner-name"
            value={form.owner_name}
            onChange={update("owner_name")}
            placeholder="Jordan Rivera"
            className="h-12 rounded-xl"
          />
        </Field>
        <Field label="Email" error={errors.email}>
          <Input
            data-testid="input-email"
            type="email"
            value={form.email}
            onChange={update("email")}
            placeholder="jordan@olivescoffee.com"
            className="h-12 rounded-xl"
          />
        </Field>
        <Field label="Phone" error={errors.phone}>
          <Input
            data-testid="input-phone"
            value={form.phone}
            onChange={update("phone")}
            placeholder="(555) 123-4567"
            className="h-12 rounded-xl"
          />
        </Field>
        <Field label="Business type / industry" error={errors.business_type}>
          <Input
            data-testid="input-business-type"
            value={form.business_type}
            onChange={update("business_type")}
            placeholder="Restaurant, retail, services…"
            className="h-12 rounded-xl"
          />
        </Field>
        <Field label="Funding interest" error={errors.funding_product}>
          <SelectField
            testId="select-product"
            placeholder="Pick one"
            value={form.funding_product}
            onChange={update("funding_product")}
            options={productOptions}
          />
        </Field>
        <div className="sm:col-span-2">
          <Field label="How soon do you need the loan?" error={errors.funding_urgency}>
            <SelectField
              testId="select-urgency"
              placeholder="Yesterday? Next month? Just looking?"
              value={form.funding_urgency}
              onChange={update("funding_urgency")}
              options={urgencyOptions}
            />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Anything else? (optional)" error={null}>
            <Textarea
              data-testid="input-notes"
              value={form.notes}
              onChange={update("notes")}
              placeholder="Tell us what you're funding or any context that helps."
              className="min-h-[100px] rounded-xl resize-none"
            />
          </Field>
        </div>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-dashed border-primary/20 bg-primary/[0.04] px-4 py-3">
        <p className="text-sm text-muted-foreground">
          Want someone to double-check your numbers before you send?
        </p>
        <button
          type="button"
          onClick={openLiveChat}
          data-testid="apply-step-chat"
          className="inline-flex shrink-0 items-center gap-2 text-sm font-semibold text-primary hover:underline"
        >
          <MessageCircle className="h-4 w-4" />
          Chat with OliveUp
        </button>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row-reverse gap-3">
        <Button
          type="submit"
          data-testid="submit-application-btn"
          disabled={submitting}
          className="flex-1 h-14 rounded-full bg-accent hover:bg-[#D1664A] text-accent-foreground hover:text-accent-foreground text-base font-semibold shadow-[0_4px_14px_0_rgb(224,122,95,0.39)] hover:-translate-y-0.5 transition-all"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Sending it over…
            </>
          ) : (
            "Get my funding options"
          )}
        </Button>
        <Button
          type="button"
          onClick={onBack}
          variant="outline"
          data-testid="back-to-result-btn"
          className="rounded-full h-14 px-6 text-base font-semibold"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to offers
        </Button>
      </div>
      <p className="text-xs text-muted-foreground text-center mt-4">
        By submitting, you agree to be contacted by an OliveUp advisor. No
        spam. Pinky promise.
      </p>
    </form>
  );
}

function Field({ label, error, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-sm font-medium text-foreground">{label}</Label>
      {children}
      {error && (
        <span className="text-xs text-destructive font-medium">{error}</span>
      )}
    </div>
  );
}

function SelectField({ testId, value, onChange, options, placeholder }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className="h-12 rounded-xl">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value} data-testid={`${testId}-${o.value}`}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function SuccessState({ onReset, prequal }) {
  return (
    <div data-testid="application-success" className="text-center py-8">
      <div className="mx-auto h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
        <CheckCircle2 className="h-8 w-8 text-primary" />
      </div>
      <h3 className="mt-6 font-display text-3xl font-bold text-foreground">
        You did it. We got it.
      </h3>
      <p className="mt-3 text-muted-foreground max-w-md mx-auto">
        A real human from the fun-loving funding team will reach out within 2
        hours during business hours. Go ahead — pour yourself a celebratory
        whatever.
      </p>
      {prequal && prequal.matched.length > 0 && (
        <p className="mt-3 text-sm text-muted-foreground">
          We&apos;re already shopping these options for you:{" "}
          <span className="font-semibold text-foreground">
            {prequal.matched.slice(0, 3).join(", ")}
          </span>
          {prequal.matched.length > 3 ? " and more." : "."}
        </p>
      )}
      <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
        <Button
          type="button"
          onClick={openLiveChat}
          data-testid="success-chat-btn"
          className="rounded-full h-12 px-6 gap-2 bg-primary hover:bg-[#3B4A1C] text-primary-foreground font-semibold"
        >
          <MessageCircle className="h-4 w-4" />
          Live chat
        </Button>
        <Button asChild variant="outline" className="rounded-full h-12 px-6 font-semibold">
          <a href={OLIVEUP_MAILTO} data-testid="success-email-btn">
            Email a follow-up question
          </a>
        </Button>
      </div>
      <Button
        onClick={onReset}
        data-testid="submit-another-btn"
        variant="outline"
        className="mt-5 rounded-full h-12 px-6"
      >
        Submit another business
      </Button>
    </div>
  );
}
